import { useState, useEffect, useMemo } from "react";

// API
import {GetBillingOffers, GetPlans} from "../../Api/Plans";

// Models
import PlanDTO from "../../Models/DTOs/Plan";
import {
    applyResolvedOffers,
    PromotionMode,
    promotionRequestForMode,
} from "../../Helpers/BillingOffers";
import {
    clearPromotionCode,
    getPromotionCode,
    rememberPromotionCode,
} from "../../Helpers/PromotionCode";

interface FetchSubscriptionPlansOptions {
    promotionMode?: PromotionMode;
}

function useFetchSubscriptionPlans(options: FetchSubscriptionPlansOptions = {}) {
    const promotionMode = options.promotionMode ?? "acquisition";
    const rememberedPromotionCode = promotionMode === "annual_upgrade" || promotionMode === "none"
        ? null
        : getPromotionCode();
    const request = useMemo(
        () => promotionRequestForMode(promotionMode, rememberedPromotionCode),
        [promotionMode, rememberedPromotionCode]
    );
    const allowedSourcesKey = request.allowedSources.join(",");
    const allowedCadencesKey = request.allowedCadences.join(",");

    const
        [ plans, setPlans ] = useState<PlanDTO[] | null>(null),
        [ plansLoading, setPlansLoading ] = useState<boolean>(false),
        [ plansError, setPlansError ] = useState<string>("");

    useEffect(() => {

        let isCancelled = false;
        const abortController = new AbortController();

        setPlansLoading(true);

        (async () => {
            try {
                const plansResult = await GetPlans(abortController);

                if (!Array.isArray(plansResult)) {
                    throw new Error("Invalid response format.");
                }

                let resolvedPlans = plansResult;
                if (request.audience) {
                    const offersResult = await GetBillingOffers(
                        abortController,
                        request.audience,
                        request.promotionCode
                    );

                    if (!(offersResult instanceof Error)) {
                        resolvedPlans = applyResolvedOffers(
                            plansResult,
                            offersResult,
                            request.allowedSources,
                            request.allowedCadences,
                            request.audience
                        );

                        if (request.promotionCode) {
                            if (offersResult.source === "targeted" && offersResult.applied_promo_code) {
                                rememberPromotionCode(offersResult.applied_promo_code);
                            } else {
                                clearPromotionCode();
                            }
                        }
                    }
                }

                if (!isCancelled) setPlans(resolvedPlans);
            } catch (err) {
                if (!isCancelled) {
                    console.error(err);
                    setPlansError("Failed to fetch subscription plans.");
                }
            } finally {
                if (!isCancelled) {
                    setPlansLoading(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [request, allowedSourcesKey, allowedCadencesKey]);

    return { plans, plansLoading, plansError };
}

export default useFetchSubscriptionPlans;
